import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Seo from "@/components/Seo";
import { toast } from "sonner";
import { api, CITIES, TREATMENT_AREAS, CONTACT } from "@/lib/api";
import { Mail, Send } from "lucide-react";

export default function ContactPage() {
    const [params] = useSearchParams();
    const preferred = params.get("clinic") || "";
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        city: "",
        treatment_area: "",
        message: "",
        preferred_clinic: preferred,
    });
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/leads", form);
            setSubmitted(true);
            toast.success("Thank you. We'll be in touch shortly.");
        } catch (err) {
            toast.error("Something went wrong. Please try again.");
        }
        setLoading(false);
    };

    return (
        <div className="page-fade pt-32 md:pt-40 pb-24">
            <Seo
                title="Book a Consultation — Aqualyx New Zealand"
                description="Book an Aqualyx consultation with a Certified Clinic in New Zealand. Discuss treatment areas, expected results and your non-surgical contouring journey."
                path="/contact"
                breadcrumbs={[
                    { name: "Home", path: "/" },
                    { name: "Book Consultation", path: "/contact" },
                ]}
                schema={{
                    "@context": "https://schema.org",
                    "@type": "ContactPage",
                    "name": "Book a Consultation — Aqualyx NZ",
                    "url": "https://aqualyxnz.co.nz/contact",
                    "mainEntity": {
                        "@type": "Organization",
                        "name": "Aqualyx New Zealand",
                        "contactPoint": {
                            "@type": "ContactPoint",
                            "contactType": "Consultation booking",
                            "email": "info@dermalis.co.nz",
                            "areaServed": "NZ",
                            "availableLanguage": ["en"]
                        }
                    }
                }}
            />
            <Helmet>
                <meta name="keywords" content="book Aqualyx consultation NZ, Aqualyx booking New Zealand, fat dissolving consultation Auckland Wellington Christchurch" />
            </Helmet>

            <section className="max-w-[1400px] mx-auto px-6 md:px-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5 space-y-6 lg:sticky lg:top-32 self-start">
                        <div className="eyebrow">Book Consultation</div>
                        <h1 className="editorial-headline text-5xl md:text-7xl">
                            Begin your<br /><span className="italic text-champagne">contouring journey.</span>
                        </h1>
                        <p className="text-ink/70 leading-relaxed">
                            Share a few details and our team will connect you with the most appropriate Aqualyx Certified Clinic in your area.
                        </p>
                        <a href={`mailto:${CONTACT.email}`} className="inline-flex items-center gap-2 text-sm hover:text-champagne">
                            <Mail size={14} strokeWidth={1.2} /> {CONTACT.email}
                        </a>
                    </div>

                    <div className="lg:col-span-7">
                        {submitted ? (
                            <div data-testid="contact-success" className="border border-champagne/40 bg-nude p-12 text-center space-y-6">
                                <div className="eyebrow text-champagne">Received</div>
                                <h2 className="editorial-headline text-5xl">Thank you.</h2>
                                <p className="text-ink/70 max-w-md mx-auto leading-relaxed">
                                    A member of our team will be in touch within one business day with next steps and a personalised clinic recommendation.
                                </p>
                                <div className="flex gap-3 justify-center flex-wrap">
                                    <Link to="/clinics" className="btn-outline">Browse Clinics</Link>
                                    <Link to="/blog" className="btn-primary">Read the Journal</Link>
                                </div>
                            </div>
                        ) : (
                            <form data-testid="consultation-form" onSubmit={submit} className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <Field label="Full name" required>
                                        <input data-testid="form-name" required value={form.name} onChange={update("name")} className="form-input" />
                                    </Field>
                                    <Field label="Email" required>
                                        <input data-testid="form-email" type="email" required value={form.email} onChange={update("email")} className="form-input" />
                                    </Field>
                                    <Field label="Phone">
                                        <input data-testid="form-phone" value={form.phone} onChange={update("phone")} className="form-input" />
                                    </Field>
                                    <Field label="Nearest city">
                                        <select data-testid="form-city" value={form.city} onChange={update("city")} className="form-input">
                                            <option value="">Select city</option>
                                            {CITIES.map((c) => <option key={c}>{c}</option>)}
                                        </select>
                                    </Field>
                                    <Field label="Treatment area" className="md:col-span-2">
                                        <select data-testid="form-area" value={form.treatment_area} onChange={update("treatment_area")} className="form-input">
                                            <option value="">Select area</option>
                                            {TREATMENT_AREAS.map((c) => <option key={c}>{c}</option>)}
                                        </select>
                                    </Field>
                                    {preferred && (
                                        <Field label="Preferred clinic" className="md:col-span-2">
                                            <input data-testid="form-clinic" value={form.preferred_clinic} onChange={update("preferred_clinic")} className="form-input" />
                                        </Field>
                                    )}
                                    <Field label="Message" className="md:col-span-2">
                                        <textarea data-testid="form-message" rows={5} value={form.message} onChange={update("message")} className="form-input resize-none" placeholder="Tell us a little about your goals (optional)" />
                                    </Field>
                                </div>
                                <p className="text-xs text-ink/50 leading-relaxed">
                                    By submitting, you agree to be contacted by an Aqualyx representative or Certified Clinic. We respect your privacy and do not share your information with third parties outside the certified network.
                                </p>
                                <button data-testid="form-submit" type="submit" disabled={loading} className="btn-primary w-full md:w-auto">
                                    {loading ? "Sending…" : <>Request Consultation <Send size={14} strokeWidth={1.5} /></>}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            </section>

            <style>{`.form-input{width:100%;background:transparent;border:1px solid rgba(197,168,128,0.4);padding:0.85rem 1rem;font-size:0.9rem;color:#111;outline:none;transition:all .3s ease;font-family:Manrope, sans-serif;}.form-input:focus{border-color:#C5A880;box-shadow:0 0 0 1px #C5A880;}`}</style>
        </div>
    );
}

function Field({ label, children, required, className = "" }) {
    return (
        <label className={`block ${className}`}>
            <span className="eyebrow block mb-2">{label}{required && " *"}</span>
            {children}
        </label>
    );
}
